import {
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box, Divider,
  Popover,
  Stack,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { ChevronDown, X } from "lucide-react";
import {

  useEffect,

  useState } from "react";

import { useTranslation } from "react-i18next";
import {
  ContentBox,
  CreateContentModalBox,
  ForbiddenLabelTypography,
  BodyInputBase,
  LinkAccordion,
  StyledError,
  TextContent,
  TitleInputBase,
  TitleTypography,
  UserInfoBox,
  UserNameTypography
} from "./CreateContentModal.styles";
import { CreateContentModalProps } from "./CreateContentModal.types";

import {
  ButtonText,
  Icon,
  CreateModalSuccess,
  Spinner,
  StyledSnackBarFavourite,
  Link,
  CommunityClusterSelect
} from "../../components";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { POST_TYPE } from "../../consts";
import { useGetProfileImageQuery } from "../../queries";
import {
  usePublishPostQuery
} from "../../queries/community";
import { useAuthStore, useNavbarStore } from "../../stores";
import { stringAvatar } from "../../utils/community";


export function CreateContentModal({
  closeDrawer,
  onClose,
  open,
  postType,
  selectedFilter,
  selectedTopic,
  ...props
}: CreateContentModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const userData = useAuthStore(state => state.userData);
  const [bodyValue, setBodyValue] = useState<string>("");
  const [linkLabel, setLinkLabel] = useState<string>("");
  const [linkValue, setLinkValue] = useState<string>("");
  const [showLinkForbiddenLabel, setShowLinkForbiddenLabel]= useState(false);
  const [showBodyForbiddenLabel, setShowBodyForbiddenLabel]= useState(false);
  const [setUrl, setSetUrl]= useState(false);
  const [textSneckBar, setTextSnackBar] = useState("");
  const setNavbarItem = useNavbarStore(state => state.setNavbarItem);

  const { data: profileImage  } = useGetProfileImageQuery({});

  const {
    data,
    mutate: mutatePublish,
    isPending: publishIsLoading,
    isError,
    isSuccess,
    error
  } = usePublishPostQuery(
    (postType === POST_TYPE.FREE) ? {
      description: bodyValue,
      linkLabel: linkLabel,
      linkValue: linkValue,
      postType,
      selectedFilter: selectedFilter,
      selectedTopic: selectedTopic
    } : {
      description: bodyValue,
      loAuthor: ("postData" in props && props.postData) ? props.postData.loAuthor : "",
      loId: ("postData" in props && props.postData) ? props.postData.loId : "",
      loType: ("postData" in props && props.postData) ? props.postData.loType : "",
      courseId: ("postData" in props && props.postData) ? props.postData.courseId : "",
      rootId: ("postData" in props && props.postData) ? props.postData.rootId : "",
      onlyLearningActivity: ("postData" in props && props.postData) ? props.postData.onlyLearningActivity : "",
      postType,
      selectedFilter: selectedFilter,
      selectedTopic: selectedTopic
    }
  );

  useEffect(()=> {
    if(isSuccess && postType === POST_TYPE.SHARED  ){
      setTextSnackBar(t("share_added")+t("community"));
      setTimeout(()=> {
        closeDrawer();
      },3000);
    }
  },[
    closeDrawer,
    isSuccess,
    postType]);
  //managing fobidden word
  useEffect(() => {
    if( !publishIsLoading && isError) {
      const errorObject = JSON.parse(error as unknown as string);
      if(errorObject.message == "Forbidden word has been detected.") {
        errorObject.errors.forEach((error: Record<string, string|Array<string>>) => {
          let errorBody = "" as string;
          switch (error.field) {

          case "body":

            errorBody = replaceForbidden(bodyValue, error.words as string[]);
            setBodyValue(errorBody);

            setShowBodyForbiddenLabel(true);
            break;

          case "attachments_link":
            setShowLinkForbiddenLabel(true);
            break;
          default:
            break;
          }
        });
      }
    }
  },[
    bodyValue,
    error,
    isError,
    publishIsLoading
  ]);

  const postFreeStyle = postType === POST_TYPE.FREE ?  {
    height: "fit-content",
    inset: "0 !important",
    margin: "auto",
    width: "fit-content"
  } : {};

  return (
    (<Popover
      className={ "popover-community" }
      anchorEl={ "anchorEl" in props ? props.anchorEl : null }
      open={ open }
      id="content-detail-share-modal"
      data-testid="content-detail-share-modal-container"
      role="dialog"
      aria-modal="true"
      aria-label={ t("content_detail_share_modal_aria_label") }
      onClose={ onClose }
      sx={ {
        ".MuiModal-backdrop":{
          visibility: isSuccess && postType === POST_TYPE.SHARED ? "hidden" : "visible"
        },

        ".MuiPaper-root.MuiPopover-paper":{
          background: theme.customColors.backgroundCard,
          border: `2px solid  ${theme.customColors.border}`,
          borderRadius: "1.125rem",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          minHeight: "fit-content !important",
          transition: "all 0.2s ease",
          minWidth: "32rem",
          [theme.breakpoints.down("sm")]: {
            minWidth: "calc(100% - 2rem)"
          },
          ...postFreeStyle

        },
        ...props.sxContainer

      } }
      { ...props }
    >
      {
        publishIsLoading ?

          <CreateContentModalBox
            container
            alignItems={ "center !important" }
            justifyContent={ "center" }
            { ...props }
          >
            <Spinner/>
          </CreateContentModalBox>
          :

          isSuccess  ?

            (
              postType === POST_TYPE.FREE
            ) ?

              (
                <CreateContentModalBox
                  container
                  { ...props }>
                  <CreateModalSuccess
                    title={ t("added_post") }
                    onClose={ ()=> {
                      closeDrawer();
                    }
                    }
                  />
                </CreateContentModalBox>

              ) :
              (
                <StyledSnackBarFavourite
                  $visible={ isSuccess && postType === POST_TYPE.SHARED }
                  sx={ {
                    maxWidth:"unset"
                  } }
                >
                  <Icon aria-hidden="true" icon={ "icon_share" } size={ 24 }/>
                  <Typography
                    aria-live={ "assertive" }
                    fontSize = { theme.spacing(1.625) }
                    fontWeight= { 500 }
                    whiteSpace={ "nowrap" }
                    color={ theme.customColors.textPrimary }
                  >
                    { textSneckBar }
                    <Link aria-hidden="true" href={ `/community?scrollTo=${data.id}` } fontWeight={ 700 }
                      onClick={ () => setNavbarItem("COMMUNITY") }>
                      { " " + t("community") }
                    </Link>
                  </Typography>
                  <Icon
                    aria-hidden="true"
                    role="button"
                    color= { theme.palette.primary?.main }
                    icon={ "close" }
                    size={ 18 }
                    onClick={ ()=> {
                      if(closeDrawer){
                        closeDrawer();
                      }
                    } }
                    onKeyDown={ (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if(closeDrawer){
                          closeDrawer();
                        }
                      }
                    } }
                  />
                </StyledSnackBarFavourite>
              ) :
            (
              <CreateContentModalBox
                container
                alignItems={ "start !important" }
                sx={ {
                  [theme.breakpoints.down("sm")]: {
                    minWidth: "calc(100% - 2rem)"
                  }
                } }
                { ...props }
              >
                {
                  isError  ?

                    <StyledError>
                      <Icon
                        color={ theme.customColors.textWarning }
                        icon="Icons_obbligatorio"
                        size={ 24 }
                      />
                      { t("forbidden_label_topic") }
                    </StyledError>

                    :
                    null
                }
                <Box
                  sx={ {
                    position: "absolute",
                    right: "1.25rem"
                  } }
                >
                  <X
                    aria-label={ t("close") }
                    role="button"
                    tabIndex={ 0 }
                    size={ "1.5rem" }
                    cursor="pointer"
                    color={ theme.customColors?.textPrimary }
                    onClick={ ()=> {
                      if(closeDrawer){
                        closeDrawer();
                      }
                    } }
                    onKeyDown={ (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if(closeDrawer){
                          closeDrawer();
                        }
                      }
                    } }
                  />
                </Box>
                <Box
                  display={ "flex" }
                  flexDirection={ "column" }
                  gap={ "24px" }
                  width="100%"
                >
                  <TextContent id="text-content">
                    <TitleTypography>
                      { postType === POST_TYPE.SHARED ? t("share_post") : t("new_publication") }
                    </TitleTypography>
                    { /*<DescriptionTypography>*/ }
                    { /*  {  postType === POST_TYPE.SHARED ? t("share_comment") : t("give_voice") }*/ }
                    { /*</DescriptionTypography>*/ }
                  </TextContent>
                  {
                    postType === POST_TYPE.FREE ?

                      <UserInfoBox aria-hidden="true">
                        <Avatar
                          alt={ `${userData?.name} ${userData?.surname} ` }
                          { ...stringAvatar(`${userData?.name} ${userData?.surname} `) }
                          sx={ { height: "40px", width: "40px", fontSize: "0.8rem" } }
                          src={ profileImage }
                        />
                        <UserNameTypography>
                          { userData?.name }  { userData?.surname }
                        </UserNameTypography>
                      </UserInfoBox> :
                      null
                  }


                  <Stack gap={ "16px" }>

                    { showBodyForbiddenLabel &&
                      <ForbiddenLabelTypography key={ "forbidden-label-body" }>
                        { t("forbidden_label_topic") }
                      </ForbiddenLabelTypography>
                    }
                    {
                      postType === POST_TYPE.SHARED ?

                        <>
                          <CommunityClusterSelect predicate={ (cluster) => cluster.is_share_enabled }></CommunityClusterSelect>
                          <Box
                            display={ "flex" }
                            flexWrap={ isMobile ? "wrap" : "nowrap" }
                            gap={ 2 }
                            alignItems={ "center" }
                          >
                            <Stack direction={ "row" } alignItems={ "center" } gap={ "8px" } flexGrow={ 1 }>
                              <Avatar
                                { ...stringAvatar(`${userData?.name} ${userData?.surname} `) }
                                aria-hidden="true"
                                sx={ {
                                  height: "2rem",
                                  width:"2rem",
                                  fontSize: "0.8rem",
                                  fontStyle: "bold",
                                  fontWeight: "700",
                                  lineHeight: "1.2rem"
                                } }
                                src={ profileImage }
                              />
                              <BodyInputBase
                                multiline
                                sx={ {
                                  flexGrow: 1,
                                  overflowY: "auto !important",
                                  "& textarea.MuiInputBase-input":{
                                    minWidth: "15rem"
                                  }
                                } }
                                value={ bodyValue }
                                onChange={ (changeEvent) => setBodyValue(changeEvent.target.value) }
                                placeholder={ t("add_new_post") }
                                hasMinHeight={ false }
                                maxHeight={ "2.75rem" }
                              />
                            </Stack>
                            <ButtonDigited
                              typology="primary"
                              aria-label={ t("send_comment") }
                              disabled={
                                !bodyValue
                              }
                              onClick={ ()=> handlePublish() }
                              onKeyDown={ (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  handlePublish();
                                }
                              } }
                            >
                              { t("publish") }
                            </ButtonDigited>
                          </Box>
                        </>

                        :

                        <BodyInputBase
                          inputProps={ { "aria-label": t("talk_about") } }
                          multiline
                          value={ bodyValue }
                          onChange={ (changeEvent) => setBodyValue(changeEvent.target.value) }
                          placeholder={ `${t("talk_about")}` }

                        />
                    }

                  </Stack>

                  {
                    postType === POST_TYPE.FREE ?
                      <ContentBox>
                        <LinkAccordion
                          expanded={ setUrl }
                          onChange={ (_, expanded) => setSetUrl(expanded) }
                          disableGutters
                        >
                          <AccordionSummary
                            expandIcon={ <ChevronDown size={ 16 } color={ theme.customColors.textPrimary } /> }
                          >
                            <Box display="flex" alignItems="center" gap={ 1 }>
                              <Icon
                                color={ theme.customColors.textPrimary }
                                icon={ "Icons_link" }
                                size={ isMobile ? "15px" : "20px" }
                              />
                              <Typography
                                color={ theme.customColors.textPrimary }
                                fontSize="16px"
                              >
                                { "Link" }
                              </Typography>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            { showLinkForbiddenLabel &&
                              <ForbiddenLabelTypography key={ "forbidden-label-link" }>
                                { t("forbidden_text") }
                              </ForbiddenLabelTypography>
                            }
                            <Stack
                              flexDirection={ "column" }
                              gap={ theme.spacing(2) }
                              width="100%"
                            >
                              <Divider/>
                              <TitleInputBase
                                value={ linkValue }
                                onChange={ (changeEvent) => {
                                  setShowLinkForbiddenLabel(false);
                                  setLinkValue(changeEvent.target.value);
                                } }
                                placeholder={ `${t("insert_url")}` }
                                sx={ {
                                  [theme.breakpoints.down("sm")]:{
                                    width: "252px"
                                  }
                                } }
                              />
                              <TitleInputBase
                                value={ linkLabel }
                                onChange={ (changeEvent) => {
                                  setShowLinkForbiddenLabel(false);
                                  const uri = changeEvent.target.value.replace(/.+\/\/|www.|\.+/g, "");
                                  const encoded = encodeURI(uri);
                                  const decoded = decodeURI(encoded);
                                  setLinkLabel(decoded);
                                } }
                                placeholder={ `${t("insert_link_title")}` }
                                sx={ {
                                  [theme.breakpoints.down("sm")]:{
                                    width: "252px"
                                  }
                                } }
                              />
                            </Stack>
                          </AccordionDetails>
                        </LinkAccordion>
                        <Stack
                          flexDirection="row"
                          justifyContent={ "center" }
                          marginTop={ "16px" }
                        >
                          <ButtonText
                            sx={ {
                              gap:theme.spacing(3),
                              maxWidth:"120px"
                            } }
                            disabled={
                              !bodyValue
                            }
                            onClick={ ()=> handlePublish() }
                          >
                            { t("publish") }
                          </ButtonText>
                        </Stack>
                      </ContentBox> :

                      null
                  }

                </Box>
              </CreateContentModalBox>
            )
      }
    </Popover>)
  );

  function handlePublish () {

    mutatePublish();
  }

  function replaceForbidden(html: string, forbiddenWords: Array<string>): string {
    let initialHtml = html;

    const resultHtml = forbiddenWords.reduce(
      (partialHtml, currentWord) => {
        var reg = new RegExp(currentWord, "gmi");
        return partialHtml.replace(reg, `<span style="color: red;"><ins><strong>${currentWord}</strong></ins></span>`);
      },
      initialHtml
    );

    return resultHtml;
  }

}
